import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API, SHOW_PRICING } from '../App';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { ChevronRight } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const ContinueJourney = () => {
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [userTier, setUserTier] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    checkUserStatus();
  }, []);

  const checkUserStatus = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { headers });
      const tier = response.data.subscription_tier || 'free';
      setUserTier(tier);
      
      // Mark that user has seen Day 30 page
      await axios.post(`${API}/beta/day30-seen`, {}, { headers }).catch(() => {});
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const isExperienceUser = userTier === 'experience' || userTier === 'premium' || userTier === 'pro';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Experience users - Identity reinforcement
  if (isExperienceUser) {
    return (
      <div className="min-h-screen bg-[#050505] noise-overlay flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="max-w-xl text-center"
        >
          {/* Decorative line */}
          <motion.div 
            initial={{ width: 0 }}
            animate={{ width: 60 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="h-px bg-[#D4AF37]/40 mx-auto mb-16"
          />

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="space-y-8"
          >
            <p className="text-[#E5E5E5] text-lg leading-relaxed font-light">
              {t('continueJourney.experience.line1')}
            </p>

            <div className="text-[#71717A] text-sm">—</div>

            <div className="space-y-2">
              <p className="text-[#E5E5E5] text-lg leading-relaxed font-light">
                {t('continueJourney.experience.line2')}
              </p>
              <p className="text-[#D4AF37] text-lg leading-relaxed font-light">
                {t('continueJourney.experience.line3')}
              </p>
            </div>

            <div className="text-[#71717A] text-sm">—</div>

            <div className="space-y-2">
              <p className="text-[#E5E5E5] text-lg leading-relaxed font-light">
                Tu ne construis plus au hasard.
              </p>
              <p className="text-[#A1A1AA] text-lg leading-relaxed font-light">
                Tu avances avec intention.
              </p>
              <p className="text-[#A1A1AA] text-lg leading-relaxed font-light">
                Avec clarté.
              </p>
            </div>

            <div className="text-[#71717A] text-sm">—</div>

            <div className="space-y-2">
              <p className="text-[#E5E5E5] text-lg leading-relaxed font-light">
                Maintenant, la clé n'est pas d'en faire plus.
              </p>
              <p className="text-white text-xl leading-relaxed font-medium">
                C'est de rester.
              </p>
            </div>

            <div className="text-[#71717A] text-sm">—</div>

            <p className="text-[#D4AF37] text-lg leading-relaxed font-light italic">
              {t('continueJourney.experience.line4')}
            </p>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="mt-16"
          >
            <Button
              onClick={() => navigate('/dashboard')}
              className="btn-gold px-10 py-4 text-sm tracking-wide"
              data-testid="continue-building-btn"
            >
              {t('continueJourney.experience.cta')}
              <ChevronRight className="ml-2" size={16} />
            </Button>
          </motion.div>

          {/* Bottom decorative line */}
          <motion.div 
            initial={{ width: 0 }}
            animate={{ width: 60 }}
            transition={{ duration: 0.8, delay: 1.5 }}
            className="h-px bg-[#D4AF37]/40 mx-auto mt-16"
          />
        </motion.div>
      </div>
    );
  }

  // Essentiel/Beta users - Decision moment
  return (
    <div className="min-h-screen bg-[#050505] noise-overlay flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2, ease: "easeOut" }}
        className="max-w-xl text-center"
      >
        {/* Decorative line */}
        <motion.div 
          initial={{ width: 0 }}
          animate={{ width: 60 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="h-px bg-[#D4AF37]/40 mx-auto mb-16"
        />

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="space-y-8"
        >
          <p className="text-[#E5E5E5] text-lg leading-relaxed font-light">
            {t('continueJourney.essentiel.line1')}
          </p>

          <p className="text-[#D4AF37] text-lg leading-relaxed font-light">
            {t('continueJourney.essentiel.line2')}
          </p>

          <div className="text-[#71717A] text-sm">—</div>

          <div className="space-y-2">
            <p className="text-[#E5E5E5] text-lg leading-relaxed font-light">
              {t('continueJourney.essentiel.line3')}
            </p>
            <p className="text-[#A1A1AA] text-lg leading-relaxed font-light">
              {t('continueJourney.essentiel.line4')}
            </p>
          </div>

          <div className="space-y-1">
            <p className="text-white text-lg leading-relaxed font-light">
              {t('continueJourney.essentiel.line5')}
            </p>
          </div>

          <div className="text-[#71717A] text-sm">—</div>

          <p className="text-[#A1A1AA] text-lg leading-relaxed font-light">
            {t('continueJourney.essentiel.line6')}
          </p>

          <p className="text-[#E5E5E5] text-lg leading-relaxed font-light">
            {t('continueJourney.essentiel.line7')}
          </p>
        </motion.div>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="mt-16 space-y-6"
        >
          {/* Primary CTA */}
          <div>
            <Button
              onClick={() => navigate(SHOW_PRICING ? '/pricing?source=jour30' : '/dashboard')}
              className="btn-gold px-10 py-4 text-sm tracking-wide"
              data-testid="continue-experience-btn"
            >
              {SHOW_PRICING ? t('continueJourney.essentiel.ctaPrimary') : 'Continuer mon parcours'}
              <ChevronRight className="ml-2" size={16} />
            </Button>
            
            {/* Subtle tagline */}
            <p className="text-[#52525B] text-xs mt-4 font-light">
              {t('continueJourney.essentiel.line8')}
            </p>
          </div>

          {/* Secondary CTA */}
          <button
            onClick={() => navigate('/dashboard')}
            className="text-[#52525B] text-sm hover:text-[#71717A] transition-colors"
            data-testid="stay-essentiel-btn"
          >
            {t('continueJourney.essentiel.ctaSecondary')}
          </button>
        </motion.div>

        {/* Bottom decorative line */}
        <motion.div 
          initial={{ width: 0 }}
          animate={{ width: 60 }}
          transition={{ duration: 0.8, delay: 1.5 }}
          className="h-px bg-[#D4AF37]/40 mx-auto mt-16"
        />
      </motion.div>
    </div>
  );
};

export default ContinueJourney;
